import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const RECENT_APPS_LIMIT = 7;

export const EMPTY_RECENT_APP_IDS: string[] = [];

interface RecentAppsStore {
  byUser: Record<string, string[]>;
  addRecentApp: (userId: string, appId: string) => void;
}

function normalizeRecentAppIds(appIds: unknown) {
  if (!Array.isArray(appIds)) {
    return EMPTY_RECENT_APP_IDS;
  }

  const seen = new Set<string>();
  const normalized: string[] = [];

  for (const appId of appIds) {
    if (typeof appId !== 'string' || seen.has(appId)) {
      continue;
    }

    seen.add(appId);
    normalized.push(appId);

    if (normalized.length >= RECENT_APPS_LIMIT) {
      break;
    }
  }

  return normalized;
}

function normalizeByUser(byUser: unknown) {
  if (!byUser || typeof byUser !== 'object') {
    return {};
  }

  const normalized: Record<string, string[]> = {};

  for (const [userId, appIds] of Object.entries(byUser)) {
    normalized[userId] = normalizeRecentAppIds(appIds);
  }

  return normalized;
}

export const useRecentAppsStore = create<RecentAppsStore>()(
  persist(
    (set, get) => ({
      byUser: {},
      addRecentApp: (userId, appId) => {
        const current = normalizeRecentAppIds(get().byUser[userId]);
        const next = normalizeRecentAppIds([appId, ...current]);
        set({ byUser: { ...get().byUser, [userId]: next } });
      },
    }),
    {
      name: 'user-portal-recent-apps',
      merge: (persistedState, currentState) => ({
        ...currentState,
        byUser: normalizeByUser(
          persistedState &&
            typeof persistedState === 'object' &&
            'byUser' in persistedState
            ? persistedState.byUser
            : {}
        ),
      }),
    }
  )
);
